import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRobotStore } from '@/stores/useRobotStore';
import { useCurrentRobot } from '@/hooks/useCurrentRobot';
import type { Robot } from '@/lib/robots-api';
import { cn } from '@/lib/utils';

function setRobotId(id: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('robot_id', id);
  window.location.href = url.toString();
}

function displayName(robot: Robot) {
  return robot.name ?? robot.id;
}

function shortName(robot: Robot) {
  const name = displayName(robot);
  return name.length > 16 ? `${name.slice(0, 14)}…` : name;
}

function StatusDot({ online, connected }: { online: boolean; connected: boolean }) {
  const color = !connected
    ? 'bg-muted/50'
    : online
    ? 'bg-green-500'
    : 'bg-red-500';
  return <span className={cn('inline-block w-1.5 h-1.5 rounded-full shrink-0', color)} />;
}

export function RobotSelector() {
  const robots = useRobotStore((s) => s.fleet);
  const connected = useRobotStore((s) => s.connected);
  const robotOnline = useRobotStore((s) => s.robotOnline);
  const active = useCurrentRobot();
  const activeId = active?.id ?? null;
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownRight, setDropdownRight] = useState(0);
  const [dropdownTop, setDropdownTop] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownRight(window.innerWidth - rect.right);
      setDropdownTop(rect.bottom + 8);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !(e.target as Element).closest('[data-robot-selector-dropdown]')
      ) {
        setIsOpen(false);
      }
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [isOpen]);

  if (robots.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => robots.length > 1 && setIsOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors select-none',
          isOpen ? 'bg-accent/10 text-accent' : 'text-muted hover:text-foreground',
          robots.length === 1 && 'cursor-default',
        )}
        title={active ? displayName(active) : undefined}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
          <rect x="3" y="11" width="18" height="10" rx="2" />
          <path d="M12 11V7" />
          <circle cx="12" cy="5" r="2" />
          <path d="M7 15h.01M17 15h.01" />
        </svg>
        {active ? shortName(active) : '—'}
        <StatusDot online={robotOnline} connected={connected} />
        {robots.length > 1 && (
          <svg
            className={cn('transition-transform', isOpen ? 'rotate-180' : '')}
            width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </button>

      {isOpen && createPortal(
        <div
          data-robot-selector-dropdown
          className="fixed z-[100] min-w-48 rounded-xl border border-border bg-surface/95 backdrop-blur-sm shadow-lg py-1"
          style={{ top: dropdownTop, right: dropdownRight }}
        >
          <div className="px-3 py-1.5 text-[11px] font-medium text-muted uppercase tracking-wider border-b border-border mb-1">
            Your Robots
          </div>
          {robots.map((robot) => {
            const isActive = robot.id === activeId;
            const showStatus = isActive;
            return (
              <button
                key={robot.id}
                onClick={() => {
                  setIsOpen(false);
                  if (!isActive) setRobotId(robot.id);
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors',
                  isActive
                    ? 'text-accent bg-accent/5'
                    : 'text-foreground hover:bg-surface-raised',
                )}
              >
                {showStatus ? (
                  <StatusDot online={robotOnline} connected={connected} />
                ) : (
                  <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0 bg-muted/30" />
                )}
                <span className="truncate flex-1">{displayName(robot)}</span>
                {isActive && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="shrink-0 text-accent">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
